
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash, UserPlus, Key, Shield, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { UserForm } from "@/components/users/UserForm";
import { PasswordForm } from "@/components/users/PasswordForm";
import { PermissionBadge } from "@/components/users/PermissionBadge";
import { SidebarProvider } from "@/components/ui/sidebar";
import UsersSidebar from "@/components/users/UsersSidebar";

const UsersPage = () => {
  const { user, isAdmin, getAllUsers, deleteUser } = useAuth();
  const [userList, setUserList] = useState(getAllUsers());
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Se não for admin, redirecionar
  if (!user || !isAdmin()) {
    return <Navigate to="/dashboard" />;
  }

  const handleAddUser = () => {
    setShowAddDialog(true);
  };

  const handleEditUser = (user: any) => {
    setSelectedUser(user);
    setShowEditDialog(true);
  };

  const handleChangePassword = (user: any) => {
    setSelectedUser(user);
    setShowPasswordDialog(true);
  };

  const handleDeleteUser = (user: any) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (selectedUser) {
      setIsLoading(true);
      const success = await deleteUser(selectedUser.id);
      if (success) {
        setUserList(getAllUsers());
        toast.success("Usuário excluído com sucesso");
      }
      setIsLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const refreshUsers = () => {
    setIsLoading(true);
    setTimeout(() => {
      setUserList(getAllUsers());
      setIsLoading(false);
      toast.success("Lista de usuários atualizada");
    }, 500);
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <UsersSidebar />
        <div className="flex-1 flex flex-col">
          <div className="h-16 bg-white shadow-sm border-b flex items-center px-6">
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-gray-800">Gerenciamento de Usuários</h1>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={refreshUsers} disabled={isLoading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
              <Button onClick={handleAddUser}>
                <UserPlus className="mr-2 h-4 w-4" />
                Novo Usuário
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-auto bg-gray-50 p-6">
            {/* Users Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">Nome</TableHead>
                    <TableHead className="w-[250px]">E-mail</TableHead>
                    <TableHead className="w-[150px]">Perfil</TableHead>
                    <TableHead>Permissões</TableHead>
                    <TableHead className="text-right w-[120px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userList.map((userItem) => (
                    <TableRow key={userItem.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{userItem.nome}</TableCell>
                      <TableCell>{userItem.email}</TableCell>
                      <TableCell>
                        <PermissionBadge role={userItem.role} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center flex-wrap gap-1">
                          <Shield className="h-4 w-4 text-slate-400 mr-1" />
                          {userItem.role === 'admin' ? (
                            <span className="text-xs text-slate-500">Acesso completo</span>
                          ) : userItem.permissions ? (
                            Object.entries(userItem.permissions)
                              .filter(([_, value]) => value)
                              .map(([key]) => (
                                <span key={key} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                  {key.replace('can', '').replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                              ))
                          ) : (
                            <span className="text-xs text-slate-500">Permissões padrão</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUser(userItem)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleChangePassword(userItem)}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          {userItem.id !== user.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(userItem)}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {userList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-gray-500">
                        {isLoading ? (
                          <div className="flex justify-center items-center">
                            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                            Carregando usuários...
                          </div>
                        ) : (
                          "Nenhum usuário encontrado."
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Usuário</DialogTitle>
            <DialogDescription>
              Preencha os dados para criar um novo usuário no sistema.
            </DialogDescription>
          </DialogHeader>
          <UserForm
            onSuccess={() => {
              setShowAddDialog(false);
              refreshUsers();
            }}
            onCancel={() => setShowAddDialog(false)}
            readOnlyPermissions={!isAdmin()}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
            <DialogDescription>
              Altere os dados do usuário selecionado.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <UserForm
              user={selectedUser}
              onSuccess={() => {
                setShowEditDialog(false);
                refreshUsers();
              }}
              onCancel={() => setShowEditDialog(false)}
              readOnlyPermissions={!isAdmin()}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
            <DialogDescription>
              Defina uma nova senha para o usuário {selectedUser?.nome}.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <PasswordForm
              userId={selectedUser.id}
              onSuccess={() => {
                setShowPasswordDialog(false);
              }}
              onCancel={() => setShowPasswordDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente o usuário{" "}
              <span className="font-bold">{selectedUser?.nome}</span> e todos os seus dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteUser} 
              className="bg-red-500 hover:bg-red-600"
              disabled={isLoading}
            >
              {isLoading ? 
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Processando...</> : 
                'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
};

export default UsersPage;
